'use client';

import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { type EventHandler, type FocusEventHandler, type JSX, type PointerEvent as ReactPointerEvent, type RefObject, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIsMounted } from '../../hooks';
import { POPUP_CONTAINER_ATTR, POPUP_TRIGGER_ATTR, findFirstFocusableChild, findFocusableChildren, getAriaDescribedBy, getAriaRole, getScrollParent, isElementTriggerWithinTarget, tick } from './helpers';
import styles from './Popup.module.css';
import { PopupContext, usePopupContext } from './PopupContext';
import { Alignment, ElementSize, type FocusAndPointerHandler, type PopupContainerProps, PopupPosition, PopupProps, Position, TriggerRectangle } from './types';
function isEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Returns a function that sets the state to the given value if it's different from the old value.
 */
function setStateFn<T extends Record<string, unknown> | null>(value: T) {
  return (old: T) => {
    if (old === value) return old;
    if (!old) return value;
    if (isEqual(old, value)) return old;
    return value;
  };
}
export const Keys = {
  Enter: 'Enter',
  Space: ' ',
  Tab: 'Tab',
  Escape: 'Escape',
  Down: 'ArrowDown',
  Up: 'ArrowUp',
  Left: 'ArrowLeft',
  Right: 'ArrowRight',
  Home: 'Home',
  End: 'End'
} as const;
function getPopupTarget() {
  const target = document.getElementById('bp-portal-target');
  if (target) return target;
  const popupTarget = document.createElement('div');
  popupTarget.id = 'bp-portal-target';
  popupTarget.style.position = 'absolute';
  popupTarget.setAttribute('role', 'popup');
  document.body.appendChild(popupTarget);
  return popupTarget;
}
export type MaybeHtml = HTMLElement | null | undefined;
function extractElementSize(rect?: DOMRect | null): ElementSize | null {
  if (!rect) return null;
  const {
    height,
    width
  } = rect;
  return {
    height,
    width
  };
}
function PopupContainer(t0) {
  const $ = _c(81);
  const {
    triggerRect,
    id,
    onCloseTransitionEnd,
    onPopupLeave,
    children
  } = t0;
  const {
    popupRef: ref,
    spyRef,
    preferredPopupPosition,
    alignPopup,
    hasHoverTrigger,
    Anchor,
    contentClassName,
    contentWrapperClassName,
    popupOpenWrapperClassName,
    anchorClassName,
    marginInPx
  } = usePopupContext();
  const [popupSize, setPopupSize] = useState(null);
  const [bodyWidth, setBodyWidth] = useState(null);
  const [bodyHeight, setBodyHeight] = useState(null);
  const [cssOpen, setCssOpen] = useState(false);
  const {
    open,
    hidePopup,
    popupRef
  } = usePopupContext();
  let t1;
  let t2;
  if ($[0] !== open) {
    t1 = () => {
      const frame = window.requestAnimationFrame(() => {
        setCssOpen(open);
      });
      return () => window.cancelAnimationFrame(frame);
    };
    t2 = [open, setCssOpen];
    $[0] = open;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== ref) {
    t3 = () => {
      const size = extractElementSize(ref?.current?.getBoundingClientRect());
      setPopupSize(setStateFn(size));
    };
    $[3] = ref;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const extractPopupSize = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const scrollContainer = getScrollParent(getPopupTarget()) ?? document.body;
      const container = scrollContainer === window ? document.body : scrollContainer as HTMLElement;
      const rect = container.getBoundingClientRect();
      setBodyWidth(rect.width);
      setBodyHeight(rect.height);
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const extractBodySize = t4;
  let t5;
  if ($[6] !== hidePopup || $[7] !== spyRef) {
    t5 = event => {
      if (event.key !== Keys.Escape) {
        return;
      }
      event.preventDefault();
      const trigger = spyRef?.current?.nextElementSibling as MaybeHtml;
      trigger?.focus();
      hidePopup();
    };
    $[6] = hidePopup;
    $[7] = spyRef;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleEscape = t5;
  let t6;
  if ($[9] !== extractPopupSize) {
    t6 = () => {
      window.requestAnimationFrame(() => {
        extractPopupSize();
        extractBodySize();
      });
    };
    $[9] = extractPopupSize;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const onWindowResize = t6;
  let t7;
  if ($[11] !== hasHoverTrigger || $[12] !== onPopupLeave) {
    t7 = event_0 => {
      if (hasHoverTrigger) {
        onPopupLeave(event_0);
      }
    };
    $[11] = hasHoverTrigger;
    $[12] = onPopupLeave;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const onPointerLeave = t7;
  let t8;
  if ($[14] !== onPopupLeave || $[15] !== popupRef || $[16] !== ref) {
    t8 = event_1 => {
      const focusableContainer = popupRef?.current?.firstElementChild?.firstElementChild;
      if (event_1.target === focusableContainer) {
        return;
      }
      if (ref?.current?.contains(event_1.relatedTarget)) {
        return;
      }
      if (isElementTriggerWithinTarget(focusableContainer, event_1.relatedTarget)) {
        return event_1.preventDefault();
      }
      onPopupLeave(event_1);
    };
    $[14] = onPopupLeave;
    $[15] = popupRef;
    $[16] = ref;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const onFocusOut = t8;
  const anchorSize = Anchor ? 17 : 0;
  let t9;
  if ($[18] !== handleEscape || $[19] !== ref) {
    t9 = () => {
      const ele = ref?.current;
      if (!ele) {
        return;
      }
      const focusableChildren = findFocusableChildren(ele);
      focusableChildren?.forEach(child => {
        child.addEventListener("keydown", handleEscape);
      });
      return () => {
        focusableChildren?.forEach(child_0 => {
          child_0.removeEventListener("keydown", handleEscape);
        });
      };
    };
    $[18] = handleEscape;
    $[19] = ref;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== children || $[22] !== handleEscape || $[23] !== ref) {
    t10 = [children, handleEscape, ref];
    $[21] = children;
    $[22] = handleEscape;
    $[23] = ref;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[25] !== onWindowResize) {
    t11 = () => {
      onWindowResize();
      window.addEventListener("resize", onWindowResize);
      return () => window.removeEventListener("resize", onWindowResize);
    };
    t12 = [onWindowResize];
    $[25] = onWindowResize;
    $[26] = t11;
    $[27] = t12;
  } else {
    t11 = $[26];
    t12 = $[27];
  }
  useLayoutEffect(t11, t12);
  let t13;
  let t14;
  if ($[28] !== onCloseTransitionEnd || $[29] !== open || $[30] !== popupOpenWrapperClassName || $[31] !== ref) {
    t13 = () => {
      if (open) {
        return;
      }
      const ele_0 = ref?.current;
      if (!popupOpenWrapperClassName) {
        return onCloseTransitionEnd();
      }
      if (!ele_0) {
        return;
      }
      ele_0.addEventListener("transitionend", onCloseTransitionEnd);
      const timeout = setTimeout(onCloseTransitionEnd, 300);
      return () => {
        clearTimeout(timeout);
        ele_0.removeEventListener("transitionend", onCloseTransitionEnd);
      };
    };
    t14 = [ref, open, onCloseTransitionEnd, popupOpenWrapperClassName];
    $[28] = onCloseTransitionEnd;
    $[29] = open;
    $[30] = popupOpenWrapperClassName;
    $[31] = ref;
    $[32] = t13;
    $[33] = t14;
  } else {
    t13 = $[32];
    t14 = $[33];
  }
  useEffect(t13, t14);
  let t15;
  if ($[34] !== alignPopup || $[35] !== anchorSize || $[36] !== bodyHeight || $[37] !== bodyWidth || $[38] !== marginInPx || $[39] !== popupSize || $[40] !== preferredPopupPosition || $[41] !== triggerRect) {
    t15 = calculatePopupPosition({
      triggerRect,
      popupSize,
      bodyWidth,
      bodyHeight,
      anchorSize,
      preferredPopupPosition,
      alignPopup,
      originalMargin: marginInPx
    });
    $[34] = alignPopup;
    $[35] = anchorSize;
    $[36] = bodyHeight;
    $[37] = bodyWidth;
    $[38] = marginInPx;
    $[39] = popupSize;
    $[40] = preferredPopupPosition;
    $[41] = triggerRect;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  const {
    x,
    y,
    anchorOffsetX: arrowPopupOffsetX,
    position,
    width
  } = t15;
  const t16 = `translate3d(${x}px, ${y}px, 0)`;
  let t17;
  if ($[43] !== t16) {
    t17 = {
      transform: t16
    };
    $[43] = t16;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== contentWrapperClassName || $[46] !== cssOpen || $[47] !== popupOpenWrapperClassName) {
    t18 = classNames(contentWrapperClassName, popupOpenWrapperClassName && {
      [popupOpenWrapperClassName]: cssOpen
    });
    $[45] = contentWrapperClassName;
    $[46] = cssOpen;
    $[47] = popupOpenWrapperClassName;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== Anchor || $[50] !== anchorClassName || $[51] !== arrowPopupOffsetX || $[52] !== position) {
    t19 = Anchor && position === Position.BOTTOM && <Anchor position={position} x={arrowPopupOffsetX} className={anchorClassName} />;
    $[49] = Anchor;
    $[50] = anchorClassName;
    $[51] = arrowPopupOffsetX;
    $[52] = position;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  if ($[54] !== alignPopup || $[55] !== width) {
    t20 = {
      ...(alignPopup === Alignment.JUSTIFY ? {
        minWidth: width,
        overflowX: "hidden"
      } : {})
    };
    $[54] = alignPopup;
    $[55] = width;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  let t21;
  if ($[57] !== id) {
    t21 = {
      [POPUP_CONTAINER_ATTR]: id
    };
    $[57] = id;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[59] !== children || $[60] !== contentClassName || $[61] !== t20 || $[62] !== t21) {
    t22 = <div tabIndex={-1} className={contentClassName} style={t20} {...t21}>{children}</div>;
    $[59] = children;
    $[60] = contentClassName;
    $[61] = t20;
    $[62] = t21;
    $[63] = t22;
  } else {
    t22 = $[63];
  }
  let t23;
  if ($[64] !== Anchor || $[65] !== anchorClassName || $[66] !== arrowPopupOffsetX || $[67] !== position) {
    t23 = Anchor && position === Position.TOP && <Anchor position={position} x={arrowPopupOffsetX} className={anchorClassName} />;
    $[64] = Anchor;
    $[65] = anchorClassName;
    $[66] = arrowPopupOffsetX;
    $[67] = position;
    $[68] = t23;
  } else {
    t23 = $[68];
  }
  let t24;
  if ($[69] !== t18 || $[70] !== t19 || $[71] !== t22 || $[72] !== t23) {
    t24 = <div className={t18}>{t19}{t22}{t23}</div>;
    $[69] = t18;
    $[70] = t19;
    $[71] = t22;
    $[72] = t23;
    $[73] = t24;
  } else {
    t24 = $[73];
  }
  let t25;
  if ($[74] !== id || $[75] !== onFocusOut || $[76] !== onPointerLeave || $[77] !== ref || $[78] !== t17 || $[79] !== t24) {
    t25 = createPortal(<div id={id} className={styles.popupContainer} ref={ref} onPointerLeave={onPointerLeave} onBlur={onFocusOut} style={t17}>{t24}</div>, getPopupTarget());
    $[74] = id;
    $[75] = onFocusOut;
    $[76] = onPointerLeave;
    $[77] = ref;
    $[78] = t17;
    $[79] = t24;
    $[80] = t25;
  } else {
    t25 = $[80];
  }
  return t25;
}
function useSetupTrigger(id, children, spyRef) {
  const $ = _c(7);
  let t0;
  if ($[0] !== id || $[1] !== spyRef) {
    t0 = () => {
      if (!spyRef?.current) {
        return;
      }
      const element = spyRef?.current?.nextElementSibling;
      if (!element) {
        return;
      }
      spyRef.current.setAttribute("id", "tooltip-spy");
      const focusableElement = findFirstFocusableChild(element);
      const targetElement = focusableElement ?? element;
      element.setAttribute(POPUP_TRIGGER_ATTR, id);
      const roleDescription = targetElement.getAttribute("aria-roledescription");
      const originalRole = roleDescription ?? getAriaRole(targetElement) ?? "button";
      targetElement.setAttribute("role", "button");
      if (!focusableElement) {
        targetElement?.setAttribute("tabindex", "0");
        targetElement?.classList.add(styles.nonFocusableOutline);
      }
      targetElement.setAttribute("aria-roledescription", originalRole);
      const ariaIds = targetElement.getAttribute("aria-describedby");
      const ariaAlreadyIncludesId = ariaIds?.includes(id);
      if (!ariaAlreadyIncludesId) {
        targetElement.setAttribute("aria-describedby", `${getAriaDescribedBy(ariaIds, id)} tooltip-spy`);
      }
    };
    $[0] = id;
    $[1] = spyRef;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== children || $[4] !== id || $[5] !== spyRef) {
    t1 = [children, id, spyRef];
    $[3] = children;
    $[4] = id;
    $[5] = spyRef;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  useEffect(t0, t1);
}
function extractTriggerRectangle(rect: DOMRect | null | undefined, offsetY: number, offsetX: number): TriggerRectangle | null {
  if (!rect) return null;
  const {
    left,
    top,
    bottom,
    width
  } = rect;
  const res = {
    left,
    top,
    bottom,
    width,
    offsetX,
    offsetY
  };
  return res;
}
type PopupContentProps = Pick<PopupProps, 'children' | 'content'> & {
  id: string;
};
function PopupContent(t0) {
  const $ = _c(96);
  const {
    children,
    content,
    id
  } = t0;
  const {
    showPopup,
    hidePopup,
    togglePopup,
    open,
    spyRef,
    popupRef,
    hasClickTrigger,
    hasFocusTrigger,
    hasHoverTrigger,
    hasKeypressTrigger,
    setInDOM
  } = usePopupContext();
  const [triggerRect, setTriggerRect] = useState(null);
  const hasTriggerRect = !!triggerRect;
  useSetupTrigger(id, children, spyRef);
  let t1;
  if ($[0] !== setInDOM || $[1] !== spyRef) {
    t1 = () => {
      const element = spyRef?.current?.nextSibling as HTMLElement | null;
      let rect = element?.getBoundingClientRect();
      if (!rect?.height) {
        rect = undefined;
      }
      let offsetY = 0;
      let offsetX = 0;
      const scrollContainer = getScrollParent(getPopupTarget()) ?? window;
      if (scrollContainer !== window) {
        const normalContainer = scrollContainer as HTMLElement;
        offsetY = -normalContainer.getBoundingClientRect().top + normalContainer.scrollTop;
        offsetX = -normalContainer.getBoundingClientRect().left + normalContainer.scrollLeft;
      }
      const next = extractTriggerRectangle(rect, offsetY, offsetX);
      setTriggerRect(setStateFn(next));
      setInDOM(true);
    };
    $[0] = setInDOM;
    $[1] = spyRef;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const setTriggerRectangle = t1;
  let t2;
  if ($[3] !== setInDOM) {
    t2 = () => {
      setTriggerRect(null);
      setInDOM(false);
    };
    $[3] = setInDOM;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const clearTriggerRectangle = t2;
  let t3;
  if ($[5] !== hasTriggerRect || $[6] !== setTriggerRectangle) {
    t3 = () => {
      if (!hasTriggerRect) {
        return;
      }
      setTriggerRectangle();
    };
    $[5] = hasTriggerRect;
    $[6] = setTriggerRectangle;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const updateTriggerRect = t3;
  let t4;
  if ($[8] !== hidePopup || $[9] !== popupRef) {
    t4 = event => {
      const popup = popupRef?.current;
      if (event.relatedTarget instanceof Node && popup?.contains(event.relatedTarget)) {
        return event.preventDefault();
      }
      hidePopup();
    };
    $[8] = hidePopup;
    $[9] = popupRef;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const onTriggerLeave = t4;
  let t5;
  if ($[11] !== hidePopup || $[12] !== spyRef) {
    t5 = event_0 => {
      if (event_0.relatedTarget instanceof Node && spyRef?.current?.nextSibling?.contains(event_0.relatedTarget)) {
        return event_0.preventDefault();
      }
      hidePopup();
    };
    $[11] = hidePopup;
    $[12] = spyRef;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const onPopupLeave = t5;
  let t6;
  if ($[14] !== onPopupLeave || $[15] !== popupRef || $[16] !== spyRef) {
    t6 = event_1 => {
      const node = event_1.target instanceof Node;
      if (node && popupRef?.current?.contains(event_1.target)) {
        return;
      }
      if (node && spyRef?.current?.nextElementSibling?.contains(event_1.target)) {
        return;
      }
      onPopupLeave(event_1);
    };
    $[14] = onPopupLeave;
    $[15] = popupRef;
    $[16] = spyRef;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const onClickOutsidePopup = t6;
  let t7;
  if ($[18] !== hidePopup || $[19] !== togglePopup) {
    t7 = event_2 => {
      if (event_2.key === Keys.Enter || event_2.key === Keys.Space) {
        event_2.preventDefault();
        return togglePopup();
      }
      if (event_2.key === Keys.Escape) {
        return hidePopup();
      }
    };
    $[18] = hidePopup;
    $[19] = togglePopup;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const onTriggerKeyDown = t7;
  let t8;
  let t9;
  if ($[21] !== hasClickTrigger || $[22] !== onClickOutsidePopup) {
    t8 = () => {
      if (!hasClickTrigger) {
        return;
      }
      document.addEventListener("pointerdown", onClickOutsidePopup);
      return () => {
        document.removeEventListener("pointerdown", onClickOutsidePopup);
      };
    };
    t9 = [hasClickTrigger, onClickOutsidePopup];
    $[21] = hasClickTrigger;
    $[22] = onClickOutsidePopup;
    $[23] = t8;
    $[24] = t9;
  } else {
    t8 = $[23];
    t9 = $[24];
  }
  useEffect(t8, t9);
  let t10;
  if ($[25] !== hasClickTrigger || $[26] !== spyRef || $[27] !== togglePopup) {
    t10 = () => {
      if (!hasClickTrigger) {
        return;
      }
      const triggerElement = spyRef?.current?.nextElementSibling as MaybeHtml;
      triggerElement?.addEventListener("pointerdown", togglePopup);
      return () => {
        triggerElement?.removeEventListener("pointerdown", togglePopup);
      };
    };
    $[25] = hasClickTrigger;
    $[26] = spyRef;
    $[27] = togglePopup;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== children || $[30] !== hasClickTrigger || $[31] !== spyRef || $[32] !== togglePopup) {
    t11 = [hasClickTrigger, togglePopup, spyRef, children];
    $[29] = children;
    $[30] = hasClickTrigger;
    $[31] = spyRef;
    $[32] = togglePopup;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  useEffect(t10, t11);
  let t12;
  if ($[34] !== hasHoverTrigger || $[35] !== onTriggerLeave || $[36] !== showPopup || $[37] !== spyRef) {
    t12 = () => {
      if (!hasHoverTrigger) {
        return;
      }
      const triggerElement_0 = spyRef?.current?.nextElementSibling as MaybeHtml;
      triggerElement_0?.addEventListener("mouseover", showPopup);
      triggerElement_0?.addEventListener("mouseleave", onTriggerLeave);
      return () => {
        triggerElement_0?.removeEventListener("mouseover", showPopup);
        triggerElement_0?.removeEventListener("mouseleave", onTriggerLeave);
      };
    };
    $[34] = hasHoverTrigger;
    $[35] = onTriggerLeave;
    $[36] = showPopup;
    $[37] = spyRef;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== children || $[40] !== hasHoverTrigger || $[41] !== onTriggerLeave || $[42] !== showPopup || $[43] !== spyRef) {
    t13 = [hasHoverTrigger, onTriggerLeave, showPopup, spyRef, children];
    $[39] = children;
    $[40] = hasHoverTrigger;
    $[41] = onTriggerLeave;
    $[42] = showPopup;
    $[43] = spyRef;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  useEffect(t12, t13);
  let t14;
  if ($[45] !== onTriggerLeave || $[46] !== spyRef) {
    t14 = () => {
      const triggerElement_1 = spyRef?.current?.nextElementSibling as MaybeHtml;
      triggerElement_1?.addEventListener("focusout", onTriggerLeave);
      return () => triggerElement_1?.removeEventListener("focusout", onTriggerLeave);
    };
    $[45] = onTriggerLeave;
    $[46] = spyRef;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  let t15;
  if ($[48] !== children || $[49] !== onTriggerLeave || $[50] !== spyRef) {
    t15 = [onTriggerLeave, spyRef, children];
    $[48] = children;
    $[49] = onTriggerLeave;
    $[50] = spyRef;
    $[51] = t15;
  } else {
    t15 = $[51];
  }
  useEffect(t14, t15);
  let t16;
  if ($[52] !== hasFocusTrigger || $[53] !== showPopup || $[54] !== spyRef) {
    t16 = () => {
      if (!hasFocusTrigger) {
        return;
      }
      const triggerElement_2 = spyRef?.current?.nextElementSibling as MaybeHtml;
      triggerElement_2?.addEventListener("focus", showPopup);
      triggerElement_2?.addEventListener("focusin", showPopup);
      return () => {
        triggerElement_2?.removeEventListener("focus", showPopup);
        triggerElement_2?.removeEventListener("focusin", showPopup);
      };
    };
    $[52] = hasFocusTrigger;
    $[53] = showPopup;
    $[54] = spyRef;
    $[55] = t16;
  } else {
    t16 = $[55];
  }
  let t17;
  if ($[56] !== children || $[57] !== hasFocusTrigger || $[58] !== showPopup || $[59] !== spyRef) {
    t17 = [hasFocusTrigger, showPopup, children, spyRef];
    $[56] = children;
    $[57] = hasFocusTrigger;
    $[58] = showPopup;
    $[59] = spyRef;
    $[60] = t17;
  } else {
    t17 = $[60];
  }
  useEffect(t16, t17);
  let t18;
  if ($[61] !== hasKeypressTrigger || $[62] !== onTriggerKeyDown || $[63] !== spyRef) {
    t18 = () => {
      if (!hasKeypressTrigger) {
        return;
      }
      const triggerElement_3 = spyRef?.current?.nextElementSibling as MaybeHtml;
      if (!triggerElement_3) {
        return;
      }
      triggerElement_3.addEventListener("keydown", onTriggerKeyDown);
      return () => {
        triggerElement_3.removeEventListener("keydown", onTriggerKeyDown);
      };
    };
    $[61] = hasKeypressTrigger;
    $[62] = onTriggerKeyDown;
    $[63] = spyRef;
    $[64] = t18;
  } else {
    t18 = $[64];
  }
  let t19;
  if ($[65] !== children || $[66] !== hasKeypressTrigger || $[67] !== onTriggerKeyDown || $[68] !== spyRef) {
    t19 = [hasKeypressTrigger, onTriggerKeyDown, spyRef, children];
    $[65] = children;
    $[66] = hasKeypressTrigger;
    $[67] = onTriggerKeyDown;
    $[68] = spyRef;
    $[69] = t19;
  } else {
    t19 = $[69];
  }
  useEffect(t18, t19);
  let t20;
  let t21;
  if ($[70] !== open || $[71] !== updateTriggerRect) {
    t20 = () => {
      if (!open) {
        return;
      }
      window.addEventListener("resize", updateTriggerRect, {
        passive: true
      });
      const scrollContainer_0 = getScrollParent(getPopupTarget()) ?? window;
      scrollContainer_0.addEventListener("scroll", updateTriggerRect, {
        passive: true
      });
      return () => {
        window.removeEventListener("resize", updateTriggerRect);
        scrollContainer_0.removeEventListener("scroll", updateTriggerRect);
      };
    };
    t21 = [open, updateTriggerRect];
    $[70] = open;
    $[71] = updateTriggerRect;
    $[72] = t20;
    $[73] = t21;
  } else {
    t20 = $[72];
    t21 = $[73];
  }
  useEffect(t20, t21);
  let t22;
  if ($[74] !== updateTriggerRect) {
    t22 = () => {
      tick().then(updateTriggerRect);
    };
    $[74] = updateTriggerRect;
    $[75] = t22;
  } else {
    t22 = $[75];
  }
  let t23;
  if ($[76] !== children || $[77] !== updateTriggerRect) {
    t23 = [children, updateTriggerRect];
    $[76] = children;
    $[77] = updateTriggerRect;
    $[78] = t23;
  } else {
    t23 = $[78];
  }
  useLayoutEffect(t22, t23);
  let t24;
  let t25;
  if ($[79] !== open || $[80] !== setTriggerRectangle) {
    t24 = () => {
      if (!open) {
        return;
      }
      tick().then(setTriggerRectangle);
    };
    t25 = [open, setTriggerRectangle];
    $[79] = open;
    $[80] = setTriggerRectangle;
    $[81] = t24;
    $[82] = t25;
  } else {
    t24 = $[81];
    t25 = $[82];
  }
  useEffect(t24, t25);
  let t26;
  if ($[83] !== spyRef) {
    t26 = <div ref={spyRef} className={styles.spyElement} />;
    $[83] = spyRef;
    $[84] = t26;
  } else {
    t26 = $[84];
  }
  let t27;
  if ($[85] !== clearTriggerRectangle || $[86] !== content || $[87] !== hasTriggerRect || $[88] !== id || $[89] !== onPopupLeave || $[90] !== triggerRect) {
    t27 = hasTriggerRect && <PopupContainer triggerRect={triggerRect} onCloseTransitionEnd={clearTriggerRectangle} id={id} onPopupLeave={onPopupLeave}>{content}</PopupContainer>;
    $[85] = clearTriggerRectangle;
    $[86] = content;
    $[87] = hasTriggerRect;
    $[88] = id;
    $[89] = onPopupLeave;
    $[90] = triggerRect;
    $[91] = t27;
  } else {
    t27 = $[91];
  }
  let t28;
  if ($[92] !== children || $[93] !== t26 || $[94] !== t27) {
    t28 = <>{t26}{children}{t27}</>;
    $[92] = children;
    $[93] = t26;
    $[94] = t27;
    $[95] = t28;
  } else {
    t28 = $[95];
  }
  return t28;
}
function generateId() {
  return Math.random().toString(36).substring(2, 15);
}
function Popup(t0) {
  const $ = _c(36);
  let children;
  let content;
  let externalId;
  let inactive;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      children,
      content,
      id: externalId,
      triggerEvents: t1,
      inactive,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = content;
    $[3] = externalId;
    $[4] = inactive;
    $[5] = props;
    $[6] = t1;
  } else {
    children = $[1];
    content = $[2];
    externalId = $[3];
    inactive = $[4];
    props = $[5];
    t1 = $[6];
  }
  let t2;
  if ($[7] !== t1) {
    t2 = t1 === undefined ? ["hover", "focus"] : t1;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const triggerEvents = t2;
  const mounted = useIsMounted();
  const [open, setOpen] = useState(false);
  const [inDOM, setInDOM] = useState(false);
  const popupRef = useRef(null);
  const spyRef = useRef(null);
  const parentContext = usePopupContext();
  const [generatedId] = useState(generateId);
  const id = externalId ?? generatedId;
  let t3;
  if ($[9] !== triggerEvents) {
    t3 = triggerEvents?.includes("click");
    $[9] = triggerEvents;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const hasClickTrigger = t3;
  let t4;
  if ($[11] !== triggerEvents) {
    t4 = triggerEvents?.includes("focus");
    $[11] = triggerEvents;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const hasFocusTrigger = t4;
  let t5;
  if ($[13] !== triggerEvents) {
    t5 = triggerEvents?.includes("hover");
    $[13] = triggerEvents;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const hasHoverTrigger = t5;
  let t6;
  if ($[15] !== triggerEvents) {
    t6 = triggerEvents?.includes("keypress");
    $[15] = triggerEvents;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const hasKeypressTrigger = t6;
  let t7;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setOpen(false);
    };
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const hidePopup = t7;
  let t8;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setOpen(true);
    };
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const showPopup = t8;
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setOpen(_temp);
    };
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const togglePopup = t9;
  if (!mounted || inactive) {
    return children;
  }
  let t10;
  if ($[20] !== hasClickTrigger || $[21] !== hasFocusTrigger || $[22] !== hasHoverTrigger || $[23] !== hasKeypressTrigger || $[24] !== inDOM || $[25] !== open || $[26] !== parentContext || $[27] !== props) {
    t10 = {
      open,
      showPopup,
      hidePopup,
      togglePopup,
      spyRef,
      popupRef,
      hasClickTrigger,
      hasHoverTrigger,
      hasFocusTrigger,
      hasKeypressTrigger,
      inDOM,
      setInDOM,
      initialised: true,
      parentContext,
      ...props
    };
    $[20] = hasClickTrigger;
    $[21] = hasFocusTrigger;
    $[22] = hasHoverTrigger;
    $[23] = hasKeypressTrigger;
    $[24] = inDOM;
    $[25] = open;
    $[26] = parentContext;
    $[27] = props;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== children || $[30] !== content || $[31] !== id) {
    t11 = <PopupContent content={content} id={id}>{children}</PopupContent>;
    $[29] = children;
    $[30] = content;
    $[31] = id;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== t10 || $[34] !== t11) {
    t12 = <PopupContext.Provider value={t10}>{t11}</PopupContext.Provider>;
    $[33] = t10;
    $[34] = t11;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  return t12;
}
function _temp(old) {
  return !old;
}
export function calculatePopupPosition({
  triggerRect,
  popupSize,
  bodyWidth,
  bodyHeight,
  originalMargin = 20,
  anchorSize = 17,
  preferredPopupPosition = Position.TOP,
  alignPopup = Alignment.CENTER
}: {
  triggerRect: TriggerRectangle | null;
  popupSize: ElementSize | null;
  bodyWidth: number | null;
  bodyHeight: number | null;
  originalMargin?: number | undefined;
  anchorSize?: number | undefined;
  preferredPopupPosition?: Position;
  alignPopup?: Alignment;
}): PopupPosition {
  let x = 0;
  let y = 0;
  let right = 0;
  let anchorOffsetX = 0;
  let position: Position = preferredPopupPosition;
  let width = popupSize?.width ?? 0;
  if (!triggerRect || !popupSize || !bodyWidth || !bodyHeight) return {
    x,
    y,
    anchorOffsetX: anchorOffsetX,
    position,
    width
  };
  let origX = 0;
  // reduce the margin if the popup is too wide to accommodate it
  let halfOfPopupWidth = popupSize.width / 2;
  const halfOfTriggerWidth = triggerRect.width / 2;
  if (alignPopup === Alignment.CENTER) {
    // center the popup in respect to the trigger, x refers to the left edge of the popup
    x = triggerRect.left + triggerRect.offsetX - halfOfPopupWidth + halfOfTriggerWidth;
  } else if (alignPopup === Alignment.LEFT || alignPopup === Alignment.JUSTIFY) {
    x = triggerRect.left + triggerRect.offsetX;
    if (alignPopup === Alignment.JUSTIFY) {
      width = triggerRect.width;
      halfOfPopupWidth = halfOfTriggerWidth;
    }
  } else if (alignPopup === Alignment.RIGHT) {
    x = triggerRect.left + triggerRect.offsetX + triggerRect.width - width;
  }
  const margin = popupSize.width > bodyWidth ? 0 : Math.min(originalMargin, (bodyWidth - width) / 2);
  origX = x;
  right = x + popupSize.width;

  // popup overflows body width
  if (x < margin) {
    // to far too the left, let's put it on at the edge of the body
    x = margin;
  } else if (right > bodyWidth - margin) {
    // to far too the right, we move it back to the left by the value of the overflow
    x -= right - (bodyWidth - margin);
  }
  if (anchorSize) {
    // arrow position should be centered in respect to the trigger.
    // The provided value is calculated in respect to the popup x position
    anchorOffsetX = origX - x + halfOfPopupWidth - anchorSize;

    // Arrow should not be detached from the popup, eat up the margin if necessary
    const arrowOffsetOverflow = anchorOffsetX + 2 * anchorSize - width;
    if (arrowOffsetOverflow > 0) {
      const offset = Math.min(arrowOffsetOverflow, margin);
      x += offset;
      anchorOffsetX -= offset;
    }
  }
  if (preferredPopupPosition === Position.TOP) {
    // put the popup above the trigger
    y = triggerRect.top + triggerRect.offsetY - popupSize.height;
    if (y < Math.abs(triggerRect.offsetY)) {
      // It doesn't fit above, so place below.
      position = Position.BOTTOM;
      y = triggerRect.bottom;
    }
  } else {
    y = triggerRect.bottom + triggerRect.offsetY;
    if (y + popupSize.height > bodyHeight + triggerRect.offsetY) {
      position = Position.TOP;
      y = triggerRect.top + triggerRect.offsetY - popupSize.height;
    }
  }
  return {
    x,
    y,
    anchorOffsetX,
    position,
    width
  };
}
export default Popup;